import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import '../Team/team.css';
import { organizationTeamPath } from '../../../constants/routes';
import { useOrganizationsQuery } from '../../../hooks/useDashboardQueries';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import {
  useAddOrganizationMemberMutation,
  useOrganizationRolesQuery,
} from '../../../hooks/useOrganizationTeamQueries';
import { TeamScope } from '../../../types/organizationTeam';
import { RoleChoice } from '../Team/TeamRolesPanel';
import { teamErrorText } from '../Team/teamErrorText';
import { ChangeList } from './components/ConfirmChangeDialog';

const ORG_PICKER_SIZE = 8;

interface Props {
  userId: string;
  onClose: () => void;
}

/** Staff: pick an organization, then one of its roles, and attach the user. */
export const AttachOrganizationDialog = ({ userId, onClose }: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search.trim(), 300);
  const [pickedOrgId, setOrgId] = useState<string | null>(null);
  const [pickedRoleId, setRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [confirming, setConfirming] = useState(false);

  const orgs = useOrganizationsQuery(1, ORG_PICKER_SIZE, debounced);
  const orgList = orgs.data?.data ?? [];
  // A new search can drop the picked organization; it then no longer counts
  // as picked (nor does its role), so nothing unseen can be submitted.
  const orgId = pickedOrgId && orgList.some((o) => o.id === pickedOrgId) ? pickedOrgId : null;
  const roleId = orgId ? pickedRoleId : null;
  const scope: TeamScope = { kind: 'org', orgId: orgId ?? '' };
  const roles = useOrganizationRolesQuery(scope, Boolean(orgId));
  // Hooks are bound to one scope; while no org is picked this mutation is never called.
  const add = useAddOrganizationMemberMutation(scope);

  const roleList = roles.data ?? [];
  const orgName = orgList.find((o) => o.id === orgId)?.name ?? '';
  const roleName = roleList.find((r) => r.id === roleId)?.name ?? '';

  const pickOrg = (id: string) => {
    setOrgId(id);
    setRoleId(null);
    setError(undefined);
  };

  const submit = async () => {
    if (!orgId || !roleId) return;
    setError(undefined);
    try {
      await add.mutateAsync({ userId, roleId });
      onClose();
    } catch (err) {
      setConfirming(false);
      setError(teamErrorText(err, t));
    }
  };

  if (confirming && orgId && roleId) {
    const none = t('dashboard.userDetails.confirm.none');
    return (
      <Dialog open onClose={add.isPending ? undefined : onClose} fullWidth maxWidth="xs">
        <DialogTitle>{t('dashboard.userDetails.confirm.title')}</DialogTitle>
        <DialogContent>
          <p className="ud-confirm-message">
            {t('dashboard.userDetails.confirm.attachMessage', { org: orgName, role: roleName })}
          </p>
          <ChangeList
            changes={[
              { label: t('dashboard.userDetails.org.title'), from: none, to: orgName },
              { label: t('dashboard.userDetails.org.role'), from: none, to: roleName },
            ]}
          />
          <div className="role-form-actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="secondary-action-btn" onClick={() => setConfirming(false)} disabled={add.isPending}>
              {t('dashboard.userDetails.confirm.back')}
            </button>
            <button type="button" className="primary-action-btn" onClick={() => void submit()} disabled={add.isPending}>
              {t('dashboard.userDetails.confirm.confirm')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('dashboard.userDetails.org.attachTitle')}</DialogTitle>
      <DialogContent>
        <div className="team-dialog-form">
          <label className="role-form-label" htmlFor="attach-org-search">
            {t('dashboard.userDetails.org.searchLabel')}
          </label>
          <input
            id="attach-org-search"
            type="search"
            className="filter-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('dashboard.userDetails.org.searchPlaceholder')}
            autoFocus
          />
          <div className="team-role-choices" role="radiogroup" aria-label={t('dashboard.userDetails.org.searchLabel')}>
            {orgs.isError ? (
              <p className="role-form-error">{t('dashboard.userDetails.org.searchError')}</p>
            ) : (
              orgList.length === 0 &&
              !orgs.isFetching && <p className="team-muted">{t('dashboard.userDetails.org.noResults')}</p>
            )}
            {orgList.map((o) => (
              <label key={o.id} className={`team-role-choice${orgId === o.id ? ' is-checked' : ''}`}>
                <input type="radio" name="attach-org" checked={orgId === o.id} onChange={() => pickOrg(o.id)} aria-label={o.name} />
                <span className="team-role-choice-name">{o.name}</span>
              </label>
            ))}
          </div>

          {orgId && (
            <fieldset className="team-role-choices">
              <legend className="role-form-label">{t('dashboard.userDetails.org.pickRole')}</legend>
              {roles.isError ? (
                <p className="role-form-error">{t('dashboard.userDetails.org.rolesError')}</p>
              ) : !roles.data ? (
                <p className="team-muted">{t('dashboard.userDetails.org.rolesLoading')}</p>
              ) : roleList.length === 0 ? (
                <p className="team-muted">
                  {t('dashboard.userDetails.org.noRoles')}{' '}
                  <Link to={organizationTeamPath(orgId)}>{t('dashboard.userDetails.org.openTeam')}</Link>
                </p>
              ) : (
                roleList.map((r) => (
                  <RoleChoice key={r.id} role={r} checked={roleId === r.id} onSelect={() => setRoleId(r.id)} name="attach-role" />
                ))
              )}
            </fieldset>
          )}

          {error && <p className="role-form-error" role="alert">{error}</p>}

          <div className="role-form-actions">
            <button type="button" className="secondary-action-btn" onClick={onClose} disabled={add.isPending}>
              {t('dashboard.roles.form.cancel')}
            </button>
            <button
              type="button"
              className="primary-action-btn"
              onClick={() => setConfirming(true)}
              disabled={!orgId || !roleId || add.isPending}
            >
              {t('dashboard.userDetails.org.attachSubmit')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
